import { useRef, useEffect, useCallback } from 'react';

export interface UseSwipeNavOptions {
  onSwipeLeft: () => void;  // next page
  onSwipeRight: () => void; // prev page
  threshold?: number;       // min px delta, default 50
  enabled?: boolean;
}

export function useSwipeNav<T extends HTMLElement = HTMLElement>({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  enabled = true,
}: UseSwipeNavOptions) {
  const containerRef = useRef<T | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled || e.touches.length !== 1) return;
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    },
    [enabled]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!enabled || touchStartX.current === null || touchStartY.current === null) return;
      if (e.changedTouches.length === 0) return;

      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      const deltaY = e.changedTouches[0].clientY - touchStartY.current;

      touchStartX.current = null;
      touchStartY.current = null;

      // Ensure horizontal swipe is dominant (not vertical scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= threshold) {
        if (deltaX < 0) {
          // Swiped left -> Next page
          onSwipeLeft();
        } else {
          // Swiped right -> Previous page
          onSwipeRight();
        }
      }
    },
    [enabled, threshold, onSwipeLeft, onSwipeRight]
  );

  useEffect(() => {
    const el = containerRef.current || window;
    el.addEventListener('touchstart', handleTouchStart as EventListener, { passive: true });
    el.addEventListener('touchend', handleTouchEnd as EventListener, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart as EventListener);
      el.removeEventListener('touchend', handleTouchEnd as EventListener);
    };
  }, [handleTouchStart, handleTouchEnd]);

  return containerRef;
}
